import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CircularProgress } from '@mui/material'
import Wizard from '../../mailTemplate/src/components/atomic/pages/Wizard'
import CampaignDashboardTable from '../../mailTemplate/src/components/atomic/organisms/CampaignDashboardTable'
import TitleFilterDialog from '../../mailTemplate/src/components/atomic/molecules/TitleFilterDialog'
import EmailEditDialog from '../../mailTemplate/src/components/atomic/molecules/EmailEditDialog'
import DeleteFirmConfirmDialog from '../../mailTemplate/src/components/atomic/molecules/DeleteFirmConfirmDialog'
import TaskOptionDialog from '../../mailTemplate/src/components/atomic/molecules/TaskOptionDialog'
import { getUrlParams } from '../../mailTemplate/src/services/urlServices'
import { useEndpointStatusActions } from '../../store/endpointStatus'
import useFirmSelection from '../hooks/useFirmSelection'
import {
  getCampaignFirms,
  startRelevancyCheck,
  startContactSearch,
  startEmailAddressQuery,
  startGenerateContent,
  getTaskStatus,
  updateCampaignFirmEmailContent,
  updateContactSelection,
  startMailing,
  deleteCampaignFirm,
  downloadCampaignFirmsExcelWithFilters
} from '../../mailTemplate/src/services/B2BServices'

const DEFAULT_PAGE = 0
const DEFAULT_PAGE_SIZE = 10
const POLL_INTERVAL = 5000
const SEARCH_DEBOUNCE_MS = 300
const ALREADY_PROCESSED_ERROR = 'FirmAlreadyProcessedExceptionDetail'

const DEFAULT_FILTERS = { search: '', relevancy: [], contactStatus: [], contentStatus: [] }
const DEFAULT_SORTING = { sortBy: null, direction: 'ASC' }

const BACKEND_TASK_TYPE_MAP = {
  RELEVANCY_CHECK: { type: 'relevancy', labelKey: 'mailTemplate.campaigns.dashboard.task.relevancyCheck' },
  CONTACT_SEARCH: { type: 'contacts', labelKey: 'mailTemplate.campaigns.dashboard.task.contactSearch' },
  CONTACT_FIRM_SEARCH: { type: 'contacts', labelKey: 'mailTemplate.campaigns.dashboard.task.contactSearch' },
  EMAIL_SEARCH: { type: 'emailQuery', labelKey: 'mailTemplate.campaigns.dashboard.task.emailAddressQuery' },
  CONTACT_EMAIL_SEARCH: { type: 'emailQuery', labelKey: 'mailTemplate.campaigns.dashboard.task.emailAddressQuery' },
  GENERATE_CONTENT: { type: 'content', labelKey: 'mailTemplate.campaigns.dashboard.task.contentGeneration' },
  SEND_EMAIL: { type: 'mailing', labelKey: 'mailTemplate.campaigns.dashboard.task.mailing' }
}

const buildBackendFilters = (filters, debouncedSearch) => {
  const params = {}

  if (debouncedSearch) {
    params.searchKeyword = debouncedSearch
  }

  if (filters.relevancy.length > 0) {
    params.suitabilityLevels = filters.relevancy.join(',')
  }

  if (filters.contactStatus.length === 1) {
    params.contactExists = filters.contactStatus[0] === 'DONE'
  }

  if (filters.contentStatus.length === 1) {
    params.contentExists = filters.contentStatus[0] === 'DONE'
  }

  return params
}

const buildFilterPayload = (filters, debouncedSearch) => {
  const filter = {}

  if (debouncedSearch) {
    filter.searchKeyword = debouncedSearch
  }

  if (filters.relevancy.length > 0) {
    filter.suitabilityLevels = filters.relevancy
  }

  if (filters.contactStatus.length === 1) {
    filter.contactExists = filters.contactStatus[0] === 'DONE'
  }

  if (filters.contentStatus.length === 1) {
    filter.contentExists = filters.contentStatus[0] === 'DONE'
  }

  return Object.keys(filter).length > 0 ? filter : null
}

const isGenericContact = (contact) => contact.free === true || contact.free === undefined

const mapFirmFromBackend = (firm, { emailQueryInProgress = false } = {}) => {
  const allContacts = firm.contacts || []
  const genericContacts = allContacts.filter(isGenericContact)
  const premiumContacts = allContacts.filter(c => !isGenericContact(c))

  let relevancyStatus = 'NOT_STARTED'
  if (firm.suitabilityLevel) {
    relevancyStatus = 'DONE'
  } else if (firm.isRelevant !== null && firm.isRelevant !== undefined) {
    relevancyStatus = 'DONE'
  }

  let genericContactStatus = 'NOT_STARTED'
  if (genericContacts.some(c => c.emailAddress)) {
    genericContactStatus = 'DONE'
  } else if (emailQueryInProgress) {
    genericContactStatus = 'IN_PROGRESS'
  }

  let premiumContactStatus = 'NOT_STARTED'
  if (premiumContacts.length > 0) {
    premiumContactStatus = 'DONE'
  }

  const contentReadyCount = allContacts.filter(c => c.selected && c.emailContent)?.length || 0
  let contentStatus = 'NOT_STARTED'
  if (contentReadyCount > 0) {
    contentStatus = 'DONE'
  }

  return {
    ...firm,
    id: firm.id || firm.identifier,
    contacts: genericContacts,
    premiumContacts,
    relevancyStatus,
    genericContactStatus,
    genericContactCount: genericContacts.length,
    genericEmailCount: genericContacts.filter(c => c.emailAddress).length,
    premiumContactStatus,
    contentStatus,
    emailReadyContactCount: firm.emailReadyContactCount || 0,
    contentReadyCount
  }
}

const CampaignFirms = ({ campaign, onCampaignUpdate, onHeaderActions, onLimitsUpdate, tourStepIndex }) => {
  const { t } = useTranslation()
  const queryParams = getUrlParams()
  const { setRetryCallback } = useEndpointStatusActions()

  const {
    selectedFirmIds, selectAllMode, excludedCount,
    deriveSelectionForPage, applySelection, updateMeta,
    toggleFirm, selectAll, clearSelection, buildPayload
  } = useFirmSelection()

  const [firms, setFirms] = useState([])
  const [statistics, setStatistics] = useState(null)
  const [loading, setLoading] = useState(true)

  const [page, setPage] = useState(DEFAULT_PAGE)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalCount, setTotalCount] = useState(0)

  const [firmsSelected, setFirmsSelected] = useState(null)
  const [isEditingFirms, setIsEditingFirms] = useState(false)

  const [titleDialogOpen, setTitleDialogOpen] = useState(false)
  const [contactSearchLoading, setContactSearchLoading] = useState(false)

  const [tasks, setTasks] = useState([])
  const [excelLoading, setExcelLoading] = useState(false)

  const [emailEdit, setEmailEdit] = useState({ open: false, contact: null, firm: null, saving: false })
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, firm: null })
  const [taskOptionDialog, setTaskOptionDialog] = useState({ open: false, apiFn: null, taskType: null, taskLabel: null, extraPayload: null, totalCount: 0, alreadyProcessedCount: 0 })

  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [sorting, setSorting] = useState(DEFAULT_SORTING)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const isMountedRef = useRef(true)
  const pollTimersRef = useRef({})
  const pollFetchRef = useRef(null)
  const tasksRef = useRef(tasks)

  const b2bCampaignId = queryParams?.b2bCampaignId

  useEffect(() => { tasksRef.current = tasks }, [tasks])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [filters.search])

  const fetchFirms = useCallback(({ fetchPage = DEFAULT_PAGE, fetchSize = DEFAULT_PAGE_SIZE, showLoading = true, currentFilters = DEFAULT_FILTERS, currentSorting = DEFAULT_SORTING, search = '' } = {}) => {
    if (!b2bCampaignId) return

    if (showLoading) setLoading(true)

    const backendFilters = buildBackendFilters(currentFilters, search)
    const sortParams = currentSorting.sortBy ? { sortBy: currentSorting.sortBy, direction: currentSorting.direction } : {}

    getCampaignFirms({ b2bCampaignId }, fetchPage, fetchSize, { ...backendFilters, ...sortParams })
      .then((res) => {
        if (!isMountedRef.current) return

        const responseData = res.data || {}
        const pageData = responseData.page || {}
        const firmList = pageData.content || []
        const emailQueryInProgress = tasksRef.current.some(t => t.type === 'emailQuery' && t.status !== 'FINISHED' && t.status !== 'FAILED')
        const mapped = firmList.map(f => mapFirmFromBackend(f, { emailQueryInProgress }))

        setFirms(mapped)
        setTotalCount(pageData.totalRecordCount || firmList.length)
        setStatistics(responseData.statistics || null)
        updateMeta(mapped)

        const derived = deriveSelectionForPage(mapped, firmList, showLoading)
        if (derived) applySelection(derived)

        if (showLoading) setFirmsSelected(firmList.length > 0)
      })
      .catch((err) => {
        console.error('Failed to fetch campaign firms:', err)
      })
      .finally(() => {
        if (isMountedRef.current) setLoading(false)
      })
  }, [b2bCampaignId, deriveSelectionForPage, applySelection, updateMeta])

  useEffect(() => {
    pollFetchRef.current = () => fetchFirms({ fetchPage: page, fetchSize: pageSize, showLoading: false, currentFilters: filters, currentSorting: sorting, search: debouncedSearch })
  }, [fetchFirms, page, pageSize, filters, sorting, debouncedSearch])

  const pollTask = useCallback((taskId, taskType, taskLabel) => {
    setTasks(prev => [
      ...prev.filter(t => t.id !== taskId),
      { id: taskId, type: taskType || 'unknown', label: taskLabel || t('mailTemplate.campaigns.dashboard.task.loading'), status: 'IN_PROGRESS', completed: 0, total: 0, loading: true }
    ])

    const poll = async () => {
      if (!isMountedRef.current) return

      try {
        const res = await getTaskStatus(taskId)
        const taskStatus = res.data
        if (!isMountedRef.current) return

        const progress = taskStatus.progress || {}
        const completed = progress.completed || 0
        const total = progress.total || 0

        if (taskStatus.limit) {
          onLimitsUpdate?.(taskStatus.limit)
        }

        const backendMapping = taskStatus.type ? BACKEND_TASK_TYPE_MAP[taskStatus.type] : null

        setTasks(prev => prev.map(task => {
          if (task.id !== taskId) return task
          const updates = { status: taskStatus.state, completed, total, loading: false }
          if (backendMapping) {
            updates.type = backendMapping.type
            updates.label = t(backendMapping.labelKey)
          }
          return { ...task, ...updates }
        }))

        pollFetchRef.current?.()

        if (taskStatus.state === 'FINISHED' || taskStatus.state === 'FAILED') {
          delete pollTimersRef.current[taskId]
        } else {
          pollTimersRef.current[taskId] = setTimeout(poll, POLL_INTERVAL)
        }
      } catch (err) {
        console.error(`Failed to poll task ${taskId}:`, err)
        if (isMountedRef.current) {
          pollTimersRef.current[taskId] = setTimeout(poll, POLL_INTERVAL)
        }
      }
    }

    poll()
  }, [onLimitsUpdate, t])

  useEffect(() => {
    isMountedRef.current = true
    fetchFirms()
    return () => {
      isMountedRef.current = false
      Object.values(pollTimersRef.current).forEach(clearTimeout)
      pollTimersRef.current = {}
      if (selectionTimerRef.current) {
        clearTimeout(selectionTimerRef.current)
        selectionTimerRef.current = null
      }
      pendingSelectionsRef.current = {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b2bCampaignId])

  useEffect(() => {
    const activeTaskIds = campaign?.activeTaskIds
    if (!activeTaskIds?.length) return
    activeTaskIds.forEach(taskId => {
      if (!pollTimersRef.current[taskId]) {
        pollTask(taskId)
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.activeTaskIds])

  const { relevancy, contactStatus, contentStatus } = filters
  useEffect(() => {
    if (!firmsSelected) return
    setPage(DEFAULT_PAGE)
    fetchFirms({ fetchSize: pageSize, showLoading: false, currentFilters: filters, currentSorting: sorting, search: debouncedSearch })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevancy, contactStatus, contentStatus, sorting, debouncedSearch])

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters)
  }, [])

  const handleSortChange = useCallback((newSorting) => {
    setSorting(newSorting)
  }, [])

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage)
    fetchFirms({ fetchPage: newPage, fetchSize: pageSize, currentFilters: filters, currentSorting: sorting, search: debouncedSearch })
  }, [fetchFirms, pageSize, filters, sorting, debouncedSearch])

  const handlePageSizeChange = useCallback((newSize) => {
    setPage(DEFAULT_PAGE)
    setPageSize(newSize)
    fetchFirms({ fetchSize: newSize, currentFilters: filters, currentSorting: sorting, search: debouncedSearch })
  }, [fetchFirms, filters, sorting, debouncedSearch])

  const handleToggleFirmSelect = useCallback((firmId, desiredState) => {
    toggleFirm(firmId, desiredState, firms)
  }, [toggleFirm, firms])

  const handleSelectAllItems = useCallback(() => {
    selectAll(firms)
  }, [selectAll, firms])

  const startTask = useCallback(async (apiFn, taskType, taskLabel, extraPayload) => {
    const tempId = `temp-${Date.now()}`

    setTasks(prev => [
      ...prev,
      { id: tempId, type: taskType, label: taskLabel, status: 'IN_PROGRESS', completed: 0, total: 0, loading: true }
    ])

    const filter = buildFilterPayload(filters, debouncedSearch)
    const payload = {
      ...buildPayload(b2bCampaignId, firms, totalCount),
      ...(filter && { filter }),
      ...extraPayload
    }

    if (!payload.option) {
      payload.option = 'ASK'
    }

    setRetryCallback((forcedFirmLimit) => {
      startTask(apiFn, taskType, taskLabel, { ...extraPayload, forcedFirmLimit })
    })

    try {
      const res = await apiFn(payload)
      const taskId = res.data
      setRetryCallback(null)
      setTasks(prev => prev.filter(t => t.id !== tempId))
      if (taskId) {
        pollTask(taskId, taskType, taskLabel)
      }
    } catch (err) {
      console.error(`Failed to start ${taskLabel}:`, err)
      setTasks(prev => prev.filter(t => t.id !== tempId))

      const errorData = err?.response?.data
      const errorType = errorData?.detail?.body?.detail?.type

      if (errorType === ALREADY_PROCESSED_ERROR) {
        const detail = errorData.detail.body.detail
        setTaskOptionDialog({
          open: true,
          apiFn,
          taskType,
          taskLabel,
          extraPayload,
          totalCount: detail.totalCount,
          alreadyProcessedCount: detail.alreadyProcessedCount
        })
      }
    }
  }, [b2bCampaignId, firms, totalCount, filters, debouncedSearch, buildPayload, pollTask, setRetryCallback])

  const handleBulkAction = useCallback(async (action) => {
    if (action === 'checkRelevancy') {
      startTask(startRelevancyCheck, 'relevancy', t('mailTemplate.campaigns.dashboard.task.relevancyCheck'))
    } else if (action === 'searchContacts') {
      setTitleDialogOpen(true)
    } else if (action === 'queryEmailAddresses') {
      startTask(startEmailAddressQuery, 'emailQuery', t('mailTemplate.campaigns.dashboard.task.emailAddressQuery'))
    } else if (action === 'generateContent') {
      startTask(startGenerateContent, 'content', t('mailTemplate.campaigns.dashboard.task.contentGeneration'))
    }
  }, [startTask, t])

  const handleContactSearchConfirm = useCallback(async (positions) => {
    setContactSearchLoading(true)
    try {
      await startTask(startContactSearch, 'contacts', t('mailTemplate.campaigns.dashboard.task.contactSearch'), { parameters: { positions } })
      setTitleDialogOpen(false)
    } finally {
      setContactSearchLoading(false)
    }
  }, [startTask, t])

  const handleContactSearchCancel = useCallback(() => {
    setTitleDialogOpen(false)
  }, [])

  const handleTaskOptionForceAll = useCallback(() => {
    const { apiFn, taskType, taskLabel, extraPayload } = taskOptionDialog
    setTaskOptionDialog({ open: false, apiFn: null, taskType: null, taskLabel: null, extraPayload: null, totalCount: 0, alreadyProcessedCount: 0 })
    startTask(apiFn, taskType, taskLabel, { ...extraPayload, option: 'FORCE_ALL' })
  }, [taskOptionDialog, startTask])

  const handleTaskOptionRemoveProcessed = useCallback(() => {
    const { apiFn, taskType, taskLabel, extraPayload } = taskOptionDialog
    setTaskOptionDialog({ open: false, apiFn: null, taskType: null, taskLabel: null, extraPayload: null, totalCount: 0, alreadyProcessedCount: 0 })
    startTask(apiFn, taskType, taskLabel, { ...extraPayload, option: 'REMOVE_ALREADY_PROCESSED' })
  }, [taskOptionDialog, startTask])

  const handleTaskOptionCancel = useCallback(() => {
    setTaskOptionDialog({ open: false, apiFn: null, taskType: null, taskLabel: null, extraPayload: null, totalCount: 0, alreadyProcessedCount: 0 })
  }, [])

  const pendingSelectionsRef = useRef({})
  const selectionTimerRef = useRef(null)

  const syncContactSelection = useCallback((firm) => {
    if (!firm || !b2bCampaignId) return
    const allSelected = [
      ...(firm.contacts || []).filter(c => c.selected),
      ...(firm.premiumContacts || []).filter(c => c.selected)
    ]
    pendingSelectionsRef.current[firm.identifier || firm.id] = allSelected.map(c => c.id)

    if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current)
    selectionTimerRef.current = setTimeout(() => {
      const pending = pendingSelectionsRef.current
      pendingSelectionsRef.current = {}
      selectionTimerRef.current = null
      const campaignFirmContacts = Object.entries(pending).map(([identifier, selectedContactIds]) => ({
        identifier,
        selectedContactIds
      }))
      if (campaignFirmContacts.length === 0) return
      updateContactSelection({
        campaignId: b2bCampaignId,
        campaignFirmContacts
      }).then(() => {
        pollFetchRef.current?.()
      }).catch(err => console.error('Failed to update contact selection:', err))
    }, 500)
  }, [b2bCampaignId])

  const handleContactToggle = useCallback((firmId, contactId) => {
    setFirms(prev => {
      const updated = prev.map(firm => {
        if (firm.id !== firmId) return firm
        const contacts = (firm.contacts || []).map(c =>
          c.id === contactId ? { ...c, selected: !c.selected } : c
        )
        return { ...firm, contacts }
      })
      syncContactSelection(updated.find(f => f.id === firmId))
      return updated
    })
  }, [syncContactSelection])

  const handlePremiumContactToggle = useCallback((firmId, contactId) => {
    setFirms(prev => {
      const updated = prev.map(firm => {
        if (firm.id !== firmId) return firm
        const premiumContacts = (firm.premiumContacts || []).map(c =>
          c.id === contactId ? { ...c, selected: !c.selected } : c
        )
        return { ...firm, premiumContacts }
      })
      syncContactSelection(updated.find(f => f.id === firmId))
      return updated
    })
  }, [syncContactSelection])

  const handleEmailPreview = useCallback(({ contact, firm }) => {
    setEmailEdit({ open: true, contact, firm, saving: false })
  }, [])

  const handleEmailEditClose = useCallback(() => {
    setEmailEdit({ open: false, contact: null, firm: null, saving: false })
  }, [])

  const handleEmailSave = useCallback(async ({ contactId, subject, html }) => {
    setEmailEdit(prev => ({ ...prev, saving: true }))
    try {
      await updateCampaignFirmEmailContent({
        campaignId: b2bCampaignId,
        contactList: [{
          contactId,
          emailSubject: subject,
          emailContent: html
        }]
      })
      handleEmailEditClose()
      fetchFirms({ fetchPage: page, fetchSize: pageSize, showLoading: false, currentFilters: filters, currentSorting: sorting, search: debouncedSearch })
    } catch (err) {
      console.error('Failed to save email content:', err)
    } finally {
      setEmailEdit(prev => ({ ...prev, saving: false }))
    }
  }, [b2bCampaignId, handleEmailEditClose, fetchFirms, page, pageSize, filters, sorting, debouncedSearch])

  const handleDeleteClick = useCallback((firm) => {
    setDeleteConfirm({ open: true, firm })
  }, [])

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirm({ open: false, firm: null })
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    const firm = deleteConfirm.firm
    if (!firm) return
    setDeleteConfirm({ open: false, firm: null })
    try {
      await deleteCampaignFirm(b2bCampaignId, firm.identifier || firm.id)
      fetchFirms({ fetchPage: page, fetchSize: pageSize, showLoading: false, currentFilters: filters, currentSorting: sorting, search: debouncedSearch })
    } catch (err) {
      console.error('Failed to delete campaign firm:', err)
    }
  }, [deleteConfirm.firm, b2bCampaignId, fetchFirms, page, pageSize, filters, sorting, debouncedSearch])

  const handleDownloadExcel = useCallback(async () => {
    setExcelLoading(true)
    try {
      const backendFilters = buildBackendFilters(filters, debouncedSearch)
      const res = await downloadCampaignFirmsExcelWithFilters(b2bCampaignId, backendFilters, sorting)
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${campaign?.name || 'campaign-firms'}.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download excel:', err)
    } finally {
      setExcelLoading(false)
    }
  }, [b2bCampaignId, filters, debouncedSearch, sorting, campaign?.name])

  const handleWizardComplete = useCallback(() => {
    setIsEditingFirms(false)
    setFirmsSelected(true)
    fetchFirms()
    if (onCampaignUpdate) onCampaignUpdate()
  }, [fetchFirms, onCampaignUpdate])

  const handleEditFirms = useCallback(() => {
    setIsEditingFirms(true)
    setFirmsSelected(false)
  }, [])

  const handleCancelEditFirms = useCallback(() => {
    setIsEditingFirms(false)
    setFirmsSelected(true)
  }, [])

  const handleSendEmails = useCallback(async () => {
    startTask(startMailing, 'mailing', t('mailTemplate.campaigns.dashboard.task.mailing'))
  }, [startTask, t])

  const hasSelection = selectAllMode === 'all' ||
    (selectAllMode === 'allExcept' && excludedCount < totalCount) ||
    Object.values(selectedFirmIds).some(Boolean)

  const hasMailingTask = tasks.some(t => t.type === 'mailing' && t.loading)

  useEffect(() => {
    if (onHeaderActions && firmsSelected) {
      onHeaderActions({ onEditFirms: handleEditFirms, onSendEmails: handleSendEmails, sendEmailsDisabled: !hasSelection || hasMailingTask, sendEmailsLoading: hasMailingTask })
    } else if (onHeaderActions) {
      onHeaderActions(null)
    }
  }, [onHeaderActions, firmsSelected, handleEditFirms, handleSendEmails, hasSelection, hasMailingTask])

  if (firmsSelected === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!firmsSelected) {
    return <Wizard onCampaignUpdate={handleWizardComplete} onCancel={isEditingFirms ? handleCancelEditFirms : undefined} />
  }

  return (
    <>
      <CampaignDashboardTable
        firms={firms}
        loading={loading}
        statistics={statistics}
        selectedFirmIds={selectedFirmIds}
        selectAllMode={selectAllMode}
        excludedCount={excludedCount}
        onToggleFirmSelect={handleToggleFirmSelect}
        onBulkAction={handleBulkAction}
        onContactToggle={handleContactToggle}
        onPremiumContactToggle={handlePremiumContactToggle}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        tasks={tasks}
        onEmailPreview={handleEmailPreview}
        onSelectAllItems={handleSelectAllItems}
        onClearSelection={clearSelection}
        filters={filters}
        onFilterChange={handleFilterChange}
        sorting={sorting}
        onSortChange={handleSortChange}
        onDeleteFirm={handleDeleteClick}
        onDownloadExcel={handleDownloadExcel}
        excelLoading={excelLoading}
        tourStepIndex={tourStepIndex}
      />
      <TitleFilterDialog
        open={titleDialogOpen}
        onConfirm={handleContactSearchConfirm}
        onCancel={handleContactSearchCancel}
        loading={contactSearchLoading}
      />
      <EmailEditDialog
        open={emailEdit.open}
        handleClose={handleEmailEditClose}
        onSave={handleEmailSave}
        contactData={emailEdit.contact}
        firmData={emailEdit.firm}
        saving={emailEdit.saving}
      />
      <DeleteFirmConfirmDialog
        open={deleteConfirm.open}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        firmName={deleteConfirm.firm?.name}
      />
      <TaskOptionDialog
        open={taskOptionDialog.open}
        taskType={taskOptionDialog.taskType}
        totalCount={taskOptionDialog.totalCount}
        alreadyProcessedCount={taskOptionDialog.alreadyProcessedCount}
        onForceAll={handleTaskOptionForceAll}
        onRemoveProcessed={handleTaskOptionRemoveProcessed}
        onCancel={handleTaskOptionCancel}
      />
    </>
  )
}

export default CampaignFirms
